import { dayClock, duration, plural } from "./format";
import { Icon } from "./icons";
import { detailHref } from "./links";
import type { DeployBoard, DeploySite } from "./model";

/**
 * The deploys view: what each target runs and how its deploys have gone, then the fleet's
 * recent deploys. The hub never deploys: the links lead to each site's workflow on GitHub.
 */
export function DeploysView({
  board,
  now,
  stale,
  deploySites,
}: {
  board: DeployBoard | null;
  now: number;
  stale: boolean;
  /** Targets the viewer may deploy; null for all. */
  deploySites: readonly string[] | null;
}) {
  if (!board) {
    return (
      <main className="view">
        <p className="muted">No target has a deploy workflow configured.</p>
      </main>
    );
  }
  const canDeploy = (target: string) => deploySites === null || deploySites.includes(target);
  return (
    <main className="view" data-stale={stale}>
      <ul className="tally" aria-label="Deploys at a glance">
        <Tally
          status={board.running > 0 ? "running" : "up"}
          value={board.running > 0 ? String(board.running) : "—"}
          label="deploying now"
        />
        <Tally
          status="up"
          value={`${board.upToDate}/${board.sites.length}`}
          label="running their branch's latest commit"
        />
        <Tally
          status={board.behind.length > 0 ? "degraded" : "up"}
          value={String(board.behind.length)}
          label="with commits not deployed yet"
          names={board.behind}
        />
        <Tally
          status={board.failed.length > 0 ? "down" : "up"}
          value={String(board.failed.length)}
          label="whose last deploy failed"
          names={board.failed}
        />
        {board.untraceable.length > 0 && (
          <Tally
            status="unknown"
            value={String(board.untraceable.length)}
            label="on an image with no commit (latest)"
            names={board.untraceable}
          />
        )}
        <Tally
          status={board.historyFailed > 0 ? "degraded" : "up"}
          value={String(board.history.length)}
          label={`deploys in 30 days${board.historyFailed > 0 ? `, ${board.historyFailed} failed` : ""}`}
        />
      </ul>

      <section className="panel" aria-labelledby="sites-title">
        <div className="panel-head">
          <h2 id="sites-title" className="eyebrow">
            Sites
          </h2>
          <span className="num">{plural(board.sites.length, "site", "sites")}</span>
        </div>
        <div className="table-scroll">
          <table className="deploy-table">
            <thead>
              <tr>
                <th scope="col">Site</th>
                <th scope="col">Running</th>
                <th scope="col">Last deploy</th>
                <th scope="col">30 days</th>
                <th scope="col">
                  <span className="sr-only">Links</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {board.sites.map((site) => (
                <SiteRow
                  key={site.target}
                  site={site}
                  now={now}
                  canDeploy={canDeploy(site.target)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel" aria-labelledby="history-title">
        <div className="panel-head">
          <h2 id="history-title" className="eyebrow">
            History
          </h2>
          <span className="num">last 30 days, newest first</span>
        </div>
        {board.history.length === 0 ? (
          <p className="muted">No deploy recorded yet.</p>
        ) : (
          <ol className="history">
            {board.history.map((d) => (
              <li key={d.key} className="history-row" data-status={d.status}>
                <Icon name={d.status} label={d.conclusion} />
                <span className="num history-when">
                  {d.finishedAt ? dayClock(d.finishedAt) : "—"}
                </span>
                <a href={detailHref(d.target)}>{d.target}</a>
                <span className="mono">{d.commit}</span>
                <span className="muted ellipsis">
                  {[d.actor, d.duration && `took ${d.duration}`, d.status !== "up" && d.conclusion]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {d.url && (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Run of ${d.target} on GitHub`}
                  >
                    ↗
                  </a>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    </main>
  );
}

function Tally({
  status,
  value,
  label,
  names = [],
}: {
  status: "up" | "degraded" | "down" | "unknown" | "running";
  value: string;
  label: string;
  names?: readonly string[];
}) {
  return (
    <li className="tally-item" data-status={status === "running" ? undefined : status}>
      {status === "running" ? (
        <span className="deploy-pulse" aria-hidden="true" />
      ) : (
        <Icon name={status} />
      )}
      <b className="num">{value}</b>
      <span>{label}</span>
      {names.length > 0 && (
        <span className="tally-names">
          {names.map((name, i) => (
            <span key={name}>
              {i > 0 && ", "}
              <a href={detailHref(name)}>{name}</a>
            </span>
          ))}
        </span>
      )}
    </li>
  );
}

function SiteRow({ site, now, canDeploy }: { site: DeploySite; now: number; canDeploy: boolean }) {
  const { version, last, running } = site;
  return (
    <tr data-running={running ? true : undefined}>
      <th scope="row">
        <a href={detailHref(site.target)}>{site.target}</a>
      </th>
      <td>
        {version ? (
          <span className="deploy-version">
            <span className="mono">{version.commit?.slice(0, 7) ?? version.running}</span>
            {version.branch?.upToDate === true && <span className="tag">up to date</span>}
            {version.branch?.upToDate === false &&
              (version.branch.compare ? (
                <a
                  className="tag tag-warn"
                  href={version.branch.compare}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  branch at {version.branch.head.slice(0, 7)} ↗
                </a>
              ) : (
                <span className="tag tag-warn">branch at {version.branch.head.slice(0, 7)}</span>
              ))}
            {!version.commit && <span className="tag">no commit in the tag</span>}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        {running ? (
          <span className="deploy-now">
            <span className="deploy-pulse" aria-hidden="true" />
            Deploying for {duration(now - running.since)}
            {running.url && (
              <a href={running.url} target="_blank" rel="noopener noreferrer">
                View ↗
              </a>
            )}
          </span>
        ) : last ? (
          <span className="deploy-last" data-status={last.status}>
            <Icon name={last.status} label={last.conclusion} />
            <span>
              {last.when}
              {last.status !== "up" && ` · ${last.conclusion}`}
            </span>
            <span className="muted">
              {[last.commit, last.actor, last.duration].filter(Boolean).join(" · ")}
            </span>
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td className="num">
        {site.count === 0
          ? "—"
          : `${site.count}${site.failed > 0 ? ` · ${site.failed} failed` : ""}`}
      </td>
      <td>
        <span className="deploy-links">
          {last?.url && (
            <a href={last.url} target="_blank" rel="noopener noreferrer">
              Run ↗
            </a>
          )}
          {canDeploy && site.deployUrl && (
            <a href={site.deployUrl} target="_blank" rel="noopener noreferrer">
              Deploy ↗
            </a>
          )}
        </span>
      </td>
    </tr>
  );
}
